import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
    RefreshCw, Search, ChevronDown, ChevronLeft, ChevronRight,
    Download, Loader2, Award, UserX, PhoneOff, CircleDot, ChevronUp,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import apiClient from "../../lib/apiClient";
import { META_PERFORMANCE_REPORT_URL, USERS_SIMPLE_URL, LEADFORMLIST } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { usePersistedState } from "../../hooks/usePersistedState";
import StateFilterSelect from "../../components/common/StateFilterSelect";
import { cn } from "../../utils/tailwindUtils";
import TruncatedText from "../../components/ui/truncated-text";
import { formatDateTime } from "../../utils/datetime";

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

// Backend expects DD-MM-YYYY
const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt   = d => d.format("DD-MM-YYYY");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                     to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),  to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")), to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")), to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: dayjs(customFrom).format("DD-MM-YYYY"), to_date: dayjs(customTo).format("DD-MM-YYYY") }
            : {};
        default:           return {};
    }
};

const getDisplayLabel = (preset, customFrom, customTo) => {
    const today = dayjs();
    switch (preset) {
        case "today":      return today.format("DD MMM YYYY");
        case "yesterday":  return today.subtract(1, "day").format("DD MMM YYYY");
        case "last_week":  return `${today.subtract(7,  "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "last_month": return `${today.subtract(30, "day").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "this_month": return `${today.startOf("month").format("DD MMM")} – ${today.format("DD MMM YYYY")}`;
        case "custom":     return (customFrom && customTo)
            ? `${dayjs(customFrom).format("DD MMM")} – ${dayjs(customTo).format("DD MMM YYYY")}`
            : null;
        default:           return null;
    }
};

// Backend sends "DD-MM-YYYY hh:mm A"

// ── bucket vocabulary — must match META_BUCKETS in customers/views.py ─────────
const BUCKETS = [
    { key: "quality",      label: "Quality",      icon: Award,
      tile:   "border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/10",
      text:   "text-emerald-700 dark:text-emerald-300",
      pill:   "bg-emerald-600 text-white border-emerald-700",
      pillOff:"bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-200",
      bar:    "bg-emerald-500",
      badge:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
      hint:   "Qualified, Demo, Proposal, Negotiation, Won" },
    { key: "disqualified", label: "Disqualified", icon: UserX,
      tile:   "border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10",
      text:   "text-red-700 dark:text-red-300",
      pill:   "bg-red-600 text-white border-red-700",
      pillOff:"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800 hover:bg-red-200",
      bar:    "bg-red-500",
      badge:  "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
      hint:   "Dis-Qualified, Closed Lost" },
    { key: "unanswered",   label: "Unanswered",   icon: PhoneOff,
      tile:   "border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10",
      text:   "text-amber-700 dark:text-amber-300",
      pill:   "bg-amber-600 text-white border-amber-700",
      pillOff:"bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800 hover:bg-amber-200",
      bar:    "bg-amber-500",
      badge:  "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
      hint:   "Unanswered, No Show" },
    { key: "open",         label: "Open",         icon: CircleDot,
      tile:   "border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/10",
      text:   "text-blue-700 dark:text-blue-300",
      pill:   "bg-blue-600 text-white border-blue-700",
      pillOff:"bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-200",
      bar:    "bg-blue-500",
      badge:  "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      hint:   "New Lead, Contacted, On Hold" },
];

const BUCKET_BY_KEY = BUCKETS.reduce((acc, b) => ({ ...acc, [b.key]: b }), {});

const pct = (v) => `${(v ?? 0).toFixed ? (v ?? 0).toFixed(1) : v}%`;

/** Stacked quality/disqualified/unanswered/open bar for one form row. */
const MixBar = ({ row }) => {
    const total = row.total || 1;
    return (
        <div className="flex h-1.5 w-full min-w-[70px] overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
            {BUCKETS.map(b => {
                const value = row[b.key] || 0;
                if (!value) return null;
                return (
                    <div
                        key={b.key}
                        className={b.bar}
                        style={{ width: `${(value * 100) / total}%` }}
                        title={`${b.label}: ${value}`}
                    />
                );
            })}
        </div>
    );
};

const MetaPerformanceReport = () => {
    const { fetch } = useApiMethods();
    const navigate  = useNavigate();

    const [summary,   setSummary]   = useState(null);
    const [byForm,    setByForm]    = useState([]);
    const [allData,   setAllData]   = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [exporting, setExporting] = useState(false);

    const [search,       setSearch]       = useState("");
    const [bucketFilter, setBucketFilter] = usePersistedState("meta_perf_bucket", null);
    const [formFilter,   setFormFilter]   = useState(null);   // client-side drill-down
    const [datePreset,   setDatePreset]   = usePersistedState("meta_perf_preset", "last_month");
    const [customFrom,   setCustomFrom]   = usePersistedState("meta_perf_from",   "");
    const [customTo,     setCustomTo]     = usePersistedState("meta_perf_to",     "");
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("meta_perf_user", "");
    const [stateFilter, setStateFilter] = usePersistedState("meta_perf_state", "");
    const [leadForms,      setLeadForms]      = useState([]);
    const [selectedFormId, setSelectedFormId] = usePersistedState("meta_perf_form", "");
    const [page,     setPage]     = useState(1);
    const [pageSize, setPageSize] = useState(25);
    // Collapsible so the lead-detail table can use the full height when needed
    const [showForms, setShowForms] = usePersistedState("meta_perf_show_forms", true);

    /** Server-side filters. Bucket + search stay client-side for the table, but
     *  are sent on export so the file matches what is on screen. */
    const buildParams = (withUiFilters = false) => {
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const params = new URLSearchParams();
        if (from_date)      params.append("from_date",        from_date);
        if (to_date)        params.append("to_date",          to_date);
        if (selectedUserId) params.append("assigned_user_id", selectedUserId);
        if (stateFilter) params.append("state", stateFilter);
        if (selectedFormId) params.append("lead_form_id",     selectedFormId);
        if (withUiFilters && bucketFilter)  params.append("bucket", bucketFilter);
        if (withUiFilters && search.trim()) params.append("search", search.trim());
        return params;
    };

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const qs  = buildParams().toString();
        const url = qs ? `${META_PERFORMANCE_REPORT_URL}?${qs}` : META_PERFORMANCE_REPORT_URL;
        fetch(url)
            .then(res => {
                if (res?.status === "success") {
                    setSummary(res.summary || null);
                    setByForm(res.by_form || []);
                    setAllData(Array.isArray(res.data) ? res.data : []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load report");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, selectedUserId, selectedFormId, stateFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
        fetch(LEADFORMLIST).then(res => {
            if (res?.status === "success") setLeadForms(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setPage(1); }, [bucketFilter, formFilter, search, pageSize]);

    const downloadExcel = async () => {
        setExporting(true);
        try {
            const params = buildParams(true);
            params.append("export_type", "excel");
            const data = await apiClient.get(`${META_PERFORMANCE_REPORT_URL}?${params.toString()}`, {
                responseType: "blob",
                timeout: 300000,
            });
            const blob = data instanceof Blob
                ? data
                : new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            const url  = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Meta_Performance_Report_${dayjs().format("YYYY-MM-DD")}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            ToastMsg("error", "Could not generate the Excel export");
        } finally {
            setExporting(false);
        }
    };

    const filtered = useMemo(() => {
        let rows = allData;
        if (bucketFilter) rows = rows.filter(r => r.bucket === bucketFilter);
        if (formFilter)   rows = rows.filter(r => r.lead_form_name === formFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(r =>
                r.customer_name?.toLowerCase().includes(q) ||
                r.contact_number?.toLowerCase().includes(q) ||
                r.city?.toLowerCase().includes(q) ||
                r.lead_form_name?.toLowerCase().includes(q) ||
                r.assigned_to_name?.toLowerCase().includes(q) ||
                r.latest_remarks?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [allData, bucketFilter, formFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <ContainerCard>
            {/* ── fixed top: header, filters, KPIs, form table ── */}
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-2 px-1">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Meta Performance Report</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Lead quality per Meta form — how many turned into quality leads vs. disqualified or never answered
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button onClick={downloadExcel} size="sm" variant="outline" className="flex items-center gap-1 text-xs"
                            disabled={exporting || loading || !allData.length}>
                            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            {exporting ? "Preparing…" : "Export Excel"}
                        </Button>
                        <Button onClick={load} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
                            <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* ── date / form / rep filters ── */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <div className="relative">
                        <select
                            value={datePreset}
                            onChange={e => {
                                setDatePreset(e.target.value);
                                if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                            }}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    {datePreset === "custom" && (
                        <>
                            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                            <span className="text-xs text-gray-400">to</span>
                            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                                className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                            <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                        </>
                    )}

                    <div className="relative">
                        <select value={selectedFormId} onChange={e => { setSelectedFormId(e.target.value); setFormFilter(null); }}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            <option value="">All Lead Forms</option>
                            {leadForms.map(f => <option key={f.id} value={f.id}>{f.form_name}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <div className="relative">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            <option value="">All Reps</option>
                            {users.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                    <StateFilterSelect value={stateFilter} onChange={setStateFilter} />

                    {datePreset !== "all" && (
                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                            {getDisplayLabel(datePreset, customFrom, customTo)}
                        </span>
                    )}
                </div>

                {/* ── KPI tiles — two lines each; stage list moved to the tooltip ── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-2">
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5"
                        title="Leads that arrived through a lead form">
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">Total Leads</p>
                        <p className="text-lg font-semibold leading-tight text-gray-800 dark:text-gray-100">{summary?.total ?? 0}</p>
                    </div>
                    {BUCKETS.map(b => {
                        const Icon = b.icon;
                        const count = summary?.[b.key] ?? 0;
                        const rate  = b.key === "open" ? null : summary?.[`${b.key}_rate`] ?? 0;
                        return (
                            <div key={b.key} className={cn("rounded-lg border px-3 py-1.5", b.tile)} title={b.hint}>
                                <p className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    <Icon className={cn("h-3 w-3", b.text)} />
                                    {b.label}
                                </p>
                                <p className={cn("text-lg font-semibold leading-tight", b.text)}>
                                    {count}
                                    {rate !== null && (
                                        <span className="ml-1 text-xs font-medium opacity-80">{pct(rate)}</span>
                                    )}
                                </p>
                            </div>
                        );
                    })}
                </div>

                {/* ── per-form performance — collapsible to free up table height ── */}
                {byForm.length > 0 && (
                    <div className="mb-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <button
                            onClick={() => setShowForms(!showForms)}
                            className="w-full flex items-center justify-between px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                            <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                                Performance by Lead Form · {byForm.length}
                            </span>
                            <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500">
                                {showForms ? "Hide" : "Show"}
                                {showForms ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            </span>
                        </button>
                        <div className={cn("overflow-auto", showForms ? "max-h-[132px]" : "hidden")}>
                            <table className="w-full text-xs">
                                <thead className="sticky top-0 z-10">
                                    <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                        <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">Lead Form</th>
                                        <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 text-center">Leads</th>
                                        <th className="px-3 py-2 font-semibold text-emerald-700 dark:text-emerald-300 text-center">Quality</th>
                                        <th className="px-3 py-2 font-semibold text-red-700 dark:text-red-300 text-center">Disqualified</th>
                                        <th className="px-3 py-2 font-semibold text-amber-700 dark:text-amber-300 text-center">Unanswered</th>
                                        <th className="px-3 py-2 font-semibold text-blue-700 dark:text-blue-300 text-center">Open</th>
                                        <th className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300 w-[120px]">Mix</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {byForm.map(f => (
                                        <tr
                                            key={f.form_id ?? f.form_name}
                                            onClick={() => setFormFilter(formFilter === f.form_name ? null : f.form_name)}
                                            className={cn(
                                                "cursor-pointer transition-colors",
                                                formFilter === f.form_name
                                                    ? "bg-indigo-50 dark:bg-indigo-900/20"
                                                    : "hover:bg-gray-50 dark:hover:bg-gray-700/40"
                                            )}
                                            title="Click to filter the lead details below"
                                        >
                                            <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-200 max-w-[240px] truncate">
                                                {f.form_name}
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-700 dark:text-gray-300">{f.total}</td>
                                            <td className="px-3 py-2 text-center text-emerald-700 dark:text-emerald-300 whitespace-nowrap">
                                                {f.quality} <span className="opacity-70">· {pct(f.quality_rate)}</span>
                                            </td>
                                            <td className="px-3 py-2 text-center text-red-700 dark:text-red-300 whitespace-nowrap">
                                                {f.disqualified} <span className="opacity-70">· {pct(f.disqualified_rate)}</span>
                                            </td>
                                            <td className="px-3 py-2 text-center text-amber-700 dark:text-amber-300 whitespace-nowrap">
                                                {f.unanswered} <span className="opacity-70">· {pct(f.unanswered_rate)}</span>
                                            </td>
                                            <td className="px-3 py-2 text-center text-blue-700 dark:text-blue-300">{f.open}</td>
                                            <td className="px-3 py-2"><MixBar row={f} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* ── bucket pills + search ── */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button
                        onClick={() => setBucketFilter(null)}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            !bucketFilter
                                ? "bg-gray-700 text-white border-gray-800"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                        )}
                    >
                        All · {summary?.total ?? allData.length}
                    </button>
                    {BUCKETS.map(b => (
                        <button
                            key={b.key}
                            onClick={() => setBucketFilter(bucketFilter === b.key ? null : b.key)}
                            className={cn(
                                "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                                bucketFilter === b.key ? b.pill : b.pillOff
                            )}
                        >
                            {b.label} · {summary?.[b.key] ?? 0}
                        </button>
                    ))}

                    {formFilter && (
                        <button
                            onClick={() => setFormFilter(null)}
                            className="px-2.5 py-1 rounded-full text-[10px] font-medium border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100"
                            title="Clear form drill-down"
                        >
                            Form: {formFilter} ✕
                        </button>
                    )}

                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Customer, mobile, city, remarks…"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-44"
                        />
                    </div>
                </div>
            </div>

            {/* ── lead details ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading report…</p>
                    </div>
                </div>
            ) : (
                <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="flex-1 min-h-0 overflow-auto">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Created</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Customer</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Mobile No</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">City</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Lead Form</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Stage</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Result</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Assigned To</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Age</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Latest Remarks</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {paginated.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No leads found
                                        </td>
                                    </tr>
                                ) : (
                                    paginated.map((row, idx) => {
                                        const bucket = BUCKET_BY_KEY[row.bucket];
                                        return (
                                            <tr
                                                key={row.id || idx}
                                                className="hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer transition-colors"
                                                onClick={() => navigate("/customers/customerdetails", {
                                                    state: { formEdit: true, row: { id: row.id } },
                                                })}
                                            >
                                                <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                    {formatDateTime(row.created_date)}
                                                </td>
                                                <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[170px]">
    <TruncatedText text={row.customer_name} />
</td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                    {row.contact_number || "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[110px] truncate">
                                                    {row.city || "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[150px]">
    <TruncatedText text={row.lead_form_name} />
</td>
                                                <td className="px-3 py-2.5">
                                                    {row.lead_stage ? (
                                                        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", getStageBadgeClass(row.lead_stage))}>
                                                            {row.lead_stage}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2.5">
                                                    {bucket ? (
                                                        <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium", bucket.badge)}>
                                                            {bucket.label}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-600 dark:text-gray-400 max-w-[120px] truncate">
                                                    {row.assigned_to_name || "—"}
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                                    {row.age_days ?? 0}d
                                                </td>
                                                <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[240px]">
    <TruncatedText text={row.latest_remarks} label="Remarks" />
</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── pagination ── */}
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(1)} disabled={page === 1}
                                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i - 1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button key={p} onClick={() => setPage(p)}
                                        className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors",
                                            p === page
                                                ? "bg-indigo-600 text-white border-indigo-600"
                                                : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}
                                      >{p}</button>
                                )
                            }
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                                className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default MetaPerformanceReport;
